import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { PaginationQueryParamsSchema } from '@terra/shared-dto-types';
import { z } from 'zod';
import { INTERNAL_NETWORK_PATH_NAME } from './internal-network.constants';
import { InternalNetworkAssessmentEntityActionGraphSchema } from './internal-network-assessment-entity-action-graph.schema';
import {
  AbortInternalNetworkAssessmentResultSchema,
  CreateInternalNetworkAssessmentSchema,
  DeleteInternalNetworkAssessmentResultSchema,
  InternalNetworkAssessmentListResponseSchema,
  InternalNetworkAssessmentSchema,
} from './internal-network-assessment.schema';

const NetworkParamsSchema = z.object({ networkId: z.string() });
const AssessmentParamsSchema = z.object({
  networkId: z.string(),
  assessmentId: z.string(),
});

export const InternalNetworkAssessmentsContract = terraContract
  .prefix(`/${INTERNAL_NETWORK_PATH_NAME}`)
  .router({
    createAssessment: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/assessments',
        summary: 'Create an internal-network assessment',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: NetworkParamsSchema,
          body: CreateInternalNetworkAssessmentSchema,
        }),
      )
      .output(InternalNetworkAssessmentSchema),
    listAssessments: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/assessments',
        summary: 'List internal-network assessments',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: NetworkParamsSchema,
          query: PaginationQueryParamsSchema,
        }),
      )
      .output(InternalNetworkAssessmentListResponseSchema),
    getAssessmentEntityActionGraph: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/assessments/{assessmentId}/entity-action-graph',
        summary: 'Get an internal-network assessment entity-action graph',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: AssessmentParamsSchema }))
      .output(InternalNetworkAssessmentEntityActionGraphSchema),
    deleteAssessment: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'DELETE',
        path: '/{networkId}/assessments/{assessmentId}',
        summary:
          'Delete an internal-network assessment (internal and dev use only)',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: AssessmentParamsSchema,
          body: z.object({}),
        }),
      )
      .output(DeleteInternalNetworkAssessmentResultSchema),
    abortAssessment: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/assessments/{assessmentId}/abort',
        summary: 'Abort a running internal-network assessment',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: AssessmentParamsSchema,
          body: z.object({}),
        }),
      )
      .output(AbortInternalNetworkAssessmentResultSchema),
  });
